import React from 'react';
import { Capacitor } from '@capacitor/core';

/** Exact brand splash green, sampled from the native splash image (#1B3A2D). */
const SPLASH_BG = '#1B3A2D';
/** The same image the native launch screens use (copied into web public). */
const SPLASH_IMAGE = '/assets/splash/splash.png';

/**
 * Dark entry splash — a pixel-match of the NATIVE launch splash so the web
 * loader reads as the splash lingering a beat longer, not a flash/swap.
 *
 * It renders the same image the OS shows, scaled the same way per platform, on
 * the exact splash green:
 *   - iOS draws the launch screen scaleAspectFill  → `object-cover`
 *   - Android shows a small centered logo via a nine-patch → `object-contain`
 *     of the square reproduces that (letterbox fills with the matching green)
 * Static — no spinner, no animation — exactly like the real splash.
 *
 * Used as the logged-out entry loader (AuthGuard), the experiment loading
 * fallback (ExperimentSwitch), and the pre-login funnel's intro beat.
 */
export const OnboardingDarkSplash: React.FC<{
  /** Optional line that fades in over the splash (intro mode). */
  caption?: React.ReactNode;
  /** Reserved for an optional escape hatch; unused while we mirror the splash. */
  onSkip?: () => void;
}> = ({ caption }) => {
  // Match each platform's native scaling so the hand-off from the OS splash to
  // this web screen is seamless (see component doc).
  const fit = Capacitor.getPlatform() === 'ios' ? 'object-cover' : 'object-contain';

  return (
    <div className="fixed inset-0 overflow-hidden" style={{ backgroundColor: SPLASH_BG }}>
      <img
        src={SPLASH_IMAGE}
        alt=""
        className={`absolute inset-0 w-full h-full ${fit}`}
        draggable={false}
      />
      {caption && (
        <div className="absolute inset-x-0 bottom-[18%] flex justify-center px-6 z-[2]">
          <p
            className="font-serif italic font-medium text-sand text-xl leading-snug max-w-[280px] text-center"
            style={{ animation: 'onbFadeIn 0.6s cubic-bezier(.16,1,.3,1)' }}
          >
            {caption}
          </p>
        </div>
      )}
    </div>
  );
};
